import { useEffect, useState } from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import KpiGrid from "./components/KpiGrid";
import Api from "utils/api";
import { decryptJSON } from "global/security";
import AdminPassPreview from "../components/AdminPassPreview";
import useWorkspaceOverview from "../workspace/useWorkspaceOverview";
import StatusBadge from "../components/StatusBadge";

export default function AdminV2Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { data: workspaceData, loading: workspaceLoading } = useWorkspaceOverview();

  const fetchKpis = async () => {
    try {
      const { response } = await Api("/super_admin_v2/kpis", "get");

      if (!response || response.success !== true) {
        setError(true);
        return;
      }

      const encryptedPayload = response.data;
      if (!encryptedPayload || typeof encryptedPayload !== "string") {
        setError(true);
        return;
      }

      const decryptedData = decryptJSON(encryptedPayload);
      if (!decryptedData) {
        setError(true);
        return;
      }

      setStats(decryptedData);
      setError(false);
    } catch (e) {
      console.error("KPI EXCEPTION:", e);
      setError(true);
    }
  };

  useEffect(() => {
    fetchKpis();
  }, []);

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-grid tw-gap-8 xl:tw-grid-cols-[minmax(0,1.2fr)_minmax(320px,380px)] xl:tw-items-center">
          <div className="tw-space-y-6">
            <div className="tw-space-y-3">
              <p className="admin-v2-eyebrow">Admin workspace</p>
              <h1 className="admin-v2-title">Track renewals, package usage, card capacity, and workspace health in one view.</h1>
              <p className="admin-v2-subtitle">
                A calmer control surface for customers, enterprises, subscriptions, vouchers, and audits, shaped around Cardly's navy, coral, and yellow guideline.
              </p>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3">
              <div className="admin-v2-brand-badge">
                <span className="admin-v2-brand-dot" />
                Cardly brand system active
              </div>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3">
              <button
                type="button"
                className="admin-v2-action admin-v2-action-primary"
                onClick={() => navigate("/admin-v2/cardholders/create")}
              >
                Create pass
                <ArrowRight size={16} />
              </button>
              <button
                type="button"
                className="admin-v2-action admin-v2-action-secondary"
                onClick={fetchKpis}
              >
                <RefreshCw size={15} />
                Refresh metrics
              </button>
            </div>

            {error && (
              <div className="admin-v2-status-note tw-p-4 tw-text-sm">
                KPI service is unavailable right now. You can still continue using Admin 2.0.
              </div>
            )}
          </div>

          <div className="tw-w-full tw-max-w-[330px] tw-justify-self-center xl:tw-justify-self-end">
            <AdminPassPreview />
          </div>
        </div>
      </section>

      {!stats && !error ? (
        <div className="admin-v2-soft-panel tw-p-6 admin-v2-text-muted">Loading dashboard metrics...</div>
      ) : (
        <KpiGrid stats={stats} />
      )}

      <div className="admin-v2-dashboard-insights">
        <section className="admin-v2-soft-panel tw-p-5 lg:tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="admin-v2-eyebrow tw-mb-2">Integrity audit</p>
              <h2 className="tw-text-xl tw-font-semibold admin-v2-text-strong">Data watch</h2>
            </div>
            <button
              type="button"
              className="tw-text-sm tw-font-medium admin-v2-text-accent"
              onClick={() => navigate("/admin-v2/audit-logs")}
            >
              Audit logs
            </button>
          </div>

          <div className="tw-mt-4 tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
            <div className="admin-v2-stat-panel">
              <p className="admin-v2-eyebrow">Total issues</p>
              <p className="tw-mt-4 tw-text-3xl tw-font-semibold admin-v2-text-strong">
                {workspaceData?.integrity?.totalFindings || 0}
              </p>
            </div>
            <div className="admin-v2-stat-panel">
              <p className="admin-v2-eyebrow">No owner</p>
              <p className="tw-mt-4 tw-text-3xl tw-font-semibold admin-v2-text-strong">
                {workspaceData?.integrity?.noOwner || 0}
              </p>
            </div>
            <div className="admin-v2-stat-panel">
              <p className="admin-v2-eyebrow">No subscription</p>
              <p className="tw-mt-4 tw-text-3xl tw-font-semibold admin-v2-text-strong">
                {workspaceData?.integrity?.noSubscription || 0}
              </p>
            </div>
            <div className="admin-v2-stat-panel">
              <p className="admin-v2-eyebrow">Missing card limit</p>
              <p className="tw-mt-4 tw-text-3xl tw-font-semibold admin-v2-text-strong">
                {workspaceData?.integrity?.missingCardLimit || 0}
              </p>
            </div>
          </div>

          <div className="tw-mt-4 tw-space-y-3">
            {workspaceLoading ? (
              <p className="tw-text-sm admin-v2-text-muted">Loading integrity audit...</p>
            ) : (workspaceData?.integrity?.rows || []).length ? (
              workspaceData.integrity.rows.slice(0, 6).map((row) => (
                <div key={row.id} className="admin-v2-stat-panel">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <div>
                      <p className="tw-font-semibold admin-v2-text-strong">{row.organizationName}</p>
                      <p className="tw-text-sm admin-v2-text-muted">
                        {row.ownerName || "No owner"} | {row.packageName || "Unknown package"}
                      </p>
                    </div>
                    <StatusBadge status={row.health || "Unknown"} />
                  </div>
                  <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                    {row.issues.map((issue) => (
                      <StatusBadge key={`${row.id}-${issue}`} status={issue} />
                    ))}
                  </div>
                  <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                    <div>
                      <p className="admin-v2-text-muted">Used</p>
                      <p className="tw-font-semibold admin-v2-text-strong">{row.usedCards}</p>
                    </div>
                    <div>
                      <p className="admin-v2-text-muted">Total</p>
                      <p className="tw-font-semibold admin-v2-text-strong">{row.totalCards}</p>
                    </div>
                    <div>
                      <p className="admin-v2-text-muted">Renewal</p>
                      <p className="tw-font-semibold admin-v2-text-strong">
                        {row.nextPaymentDate ? new Date(row.nextPaymentDate).toLocaleDateString() : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-v2-stat-panel">
                <p className="tw-text-sm admin-v2-text-muted">
                  No integrity issues found in the current workspace snapshot.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="admin-v2-soft-panel tw-p-5 lg:tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="admin-v2-eyebrow tw-mb-2">Expiring soon</p>
              <h2 className="tw-text-xl tw-font-semibold admin-v2-text-strong">Renewal watch</h2>
            </div>
            <button type="button" className="tw-text-sm tw-font-medium admin-v2-text-accent" onClick={() => navigate("/admin-v2/subscriptions")}>
              View all
            </button>
          </div>

          <div className="tw-mt-4 tw-space-y-3">
            {workspaceLoading ? (
              <p className="tw-text-sm admin-v2-text-muted">Loading subscription watch...</p>
            ) : (
              (workspaceData?.subscriptions || [])
                .filter((row) => row.daysLeft <= 14)
                .slice(0, 5)
                .map((row) => (
                  <div key={row.id} className="admin-v2-stat-panel">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                      <div>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.organizationName}</p>
                        <p className="tw-text-sm admin-v2-text-muted">{row.packageName}</p>
                      </div>
                      <StatusBadge status={row.health} />
                    </div>
                    <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                      <div>
                        <p className="admin-v2-text-muted">Days left</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.daysLeft}</p>
                      </div>
                      <div>
                        <p className="admin-v2-text-muted">Used</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.usedCards}</p>
                      </div>
                      <div>
                        <p className="admin-v2-text-muted">Remaining</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.remainingCards}</p>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </section>

        <section className="admin-v2-soft-panel tw-p-5 lg:tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="admin-v2-eyebrow tw-mb-2">Capacity</p>
              <h2 className="tw-text-xl tw-font-semibold admin-v2-text-strong">Card usage pressure</h2>
            </div>
            <button type="button" className="tw-text-sm tw-font-medium admin-v2-text-strong" onClick={() => navigate("/admin-v2/customers")}>
              Customers
            </button>
          </div>

          <div className="tw-mt-4 tw-space-y-3">
            {workspaceLoading ? (
              <p className="tw-text-sm admin-v2-text-muted">Loading capacity watch...</p>
            ) : (
              (workspaceData?.customers || [])
                .slice()
                .sort((a, b) => a.remainingCards - b.remainingCards)
                .slice(0, 5)
                .map((row) => (
                  <div key={row.id} className="admin-v2-stat-panel">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                      <div>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.organizationName}</p>
                        <p className="tw-text-sm admin-v2-text-muted">{row.name}</p>
                      </div>
                      <div className="tw-text-right">
                        <p className="tw-text-sm admin-v2-text-muted">Total</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{row.totalCards}</p>
                      </div>
                    </div>
                    <div className="admin-v2-progress tw-mt-3">
                      <span
                        className="tw-block tw-h-full tw-rounded-full tw-bg-[linear-gradient(90deg,#F35658,#FAD845)]"
                        style={{
                          width: `${row.totalCards > 0 ? Math.min((row.usedCards / row.totalCards) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                    <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-text-sm">
                      <span className="admin-v2-text-muted">{row.usedCards} used</span>
                      <span className="tw-font-semibold admin-v2-text-strong">{row.remainingCards} left</span>
                    </div>
                  </div>
                ))
            )}
          </div>
        </section>

        <section className="admin-v2-soft-panel tw-p-5 lg:tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="admin-v2-eyebrow tw-mb-2">Vouchers</p>
              <h2 className="tw-text-xl tw-font-semibold admin-v2-text-strong">Offer activity</h2>
            </div>
            <button type="button" className="tw-text-sm tw-font-medium admin-v2-text-accent" onClick={() => navigate("/admin-v2/vouchers")}>
              Vouchers
            </button>
          </div>

          <div className="tw-mt-4 tw-space-y-3">
            {workspaceLoading ? (
              <p className="tw-text-sm admin-v2-text-muted">Loading voucher activity...</p>
            ) : (
              (workspaceData?.vouchers || [])
                .slice(0, 5)
                .map((voucher) => (
                  <div key={voucher.id} className="admin-v2-stat-panel">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                      <div>
                        <p className="tw-font-semibold admin-v2-text-strong">{voucher.name}</p>
                        <p className="tw-text-sm admin-v2-text-muted">{voucher.code}</p>
                      </div>
                      <StatusBadge status={voucher.status} />
                    </div>
                    <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                      <div>
                        <p className="admin-v2-text-muted">Discount</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{voucher.percent}%</p>
                      </div>
                      <div>
                        <p className="admin-v2-text-muted">Used</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{voucher.used}</p>
                      </div>
                      <div>
                        <p className="admin-v2-text-muted">Days left</p>
                        <p className="tw-font-semibold admin-v2-text-strong">{voucher.daysLeft}</p>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
